import * as React from "react";
import { getLocalizedText, getMonthYearName, getTalkVenue } from "../util";
import type { TalkItem } from "../../types/data";
import { useLang } from "../lang";

type TalksProps = {
  items: TalkItem[];
  title_en?: string;
  title_ja?: string;
  item_nums?: number;
};

const Talks: React.FC<TalksProps> = ({ items, title_en, title_ja, item_nums }) => {
  const { langEn } = useLang();
  const [expanded, setExpanded] = React.useState(false);
  const title = langEn ? title_en : title_ja;
  const sortByPriorityYearMonthDesc = React.useCallback((a: TalkItem, b: TalkItem) => {
    const ap = a.priority ?? 0;
    const bp = b.priority ?? 0;
    if (ap !== bp) return bp - ap;
    if (a.year !== b.year) return (b.year ?? 0) - (a.year ?? 0);
    if (a.month !== b.month) return (b.month ?? 0) - (a.month ?? 0);
    return 0;
  }, []);
  const yearSortedItems = React.useMemo(
    () =>
      items.slice().sort((a, b) => {
        if (a.year !== b.year) return (b.year ?? 0) - (a.year ?? 0);
        if (a.month !== b.month) return (b.month ?? 0) - (a.month ?? 0);
        return 0;
      }),
    [items]
  );
  const selectedItems = React.useMemo(
    () => items.filter((item) => item.selected).sort(sortByPriorityYearMonthDesc),
    [items, sortByPriorityYearMonthDesc]
  );
  const collapsedItems = React.useMemo(() => {
    if (selectedItems.length > 0) return selectedItems;
    if (!item_nums) return yearSortedItems;

    const highlighted = items
      .filter((item) => (item.priority ?? 0) > 0)
      .sort(sortByPriorityYearMonthDesc);

    const merged: TalkItem[] = [...highlighted];
    for (const item of yearSortedItems) {
      if (merged.includes(item)) continue;
      merged.push(item);
    }
    return merged.slice(0, item_nums);
  }, [item_nums, items, selectedItems, sortByPriorityYearMonthDesc, yearSortedItems]);
  const displayItems = expanded ? yearSortedItems : collapsedItems;
  const showToggle = yearSortedItems.length > collapsedItems.length;
  const toggle = () => setExpanded((prev) => !prev);
  const isSelectedMode = !expanded && selectedItems.length > 0;

  return (
    <div className="col-md-12 mb-2">
      {title ? <p className="sub-title">{title}</p> : ""}
      {isSelectedMode ? <p className="h5 mb-1">{langEn ? "Selected" : "Selected"}</p> : ""}
      <ul className="mb-0">
        {displayItems.map((item, i) => (
          <li className="mb-1" key={i}>
            {getLocalizedText(item.title, langEn)}

            {item.month ? (
              <span className="mr-2"> ({getMonthYearName(item.month, item.year, langEn)})</span>
            ) : (
              ""
            )}

            {item.url ? (
              <a
                href={getLocalizedText(item.url, langEn)}
                className="academic-icon fas fa-home"
                data-toggle="tooltip"
                data-placement="top"
                title="website"
              ></a>
            ) : (
              ""
            )}

            {item.speakerdeck ? (
              <a
                className="academic-icon fab fa-speaker-deck"
                data-toggle="tooltip"
                data-placement="top"
                title="slide"
                href={item.speakerdeck}
              ></a>
            ) : (
              ""
            )}

            {item.talk ? (
              <a
                href={item.talk}
                className="academic-icon fas fa-video"
                data-toggle="tooltip"
                data-placement="top"
                title="recording"
              ></a>
            ) : (
              ""
            )}

            <br />

            <small>{getTalkVenue(item, langEn)}</small>

            {item.others ? (
              <small>
                <br />
                <em>{getLocalizedText(item.others, langEn)}</em>
              </small>
            ) : (
              ""
            )}
          </li>
        ))}
        {showToggle && !expanded ? (
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              toggle();
            }}
          >
            {langEn ? "all »" : "全件 »"}
          </a>
        ) : (
          ""
        )}
        {showToggle && expanded ? (
          <a
            href=""
            onClick={(e) => {
              e.preventDefault();
              toggle();
            }}
          >
            {langEn ? "« close" : "« 閉じる"}
          </a>
        ) : (
          ""
        )}
      </ul>
    </div>
  );
};

export default Talks;
